import { FC, useEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Handle, Position, useReactFlow } from "@xyflow/react";
import { motion } from "framer-motion";
import __ from "src/utils/Translations";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import { useStructureStore } from "src/Pages/Company/OrganizationStructure/context/useStructureStore";
import useOrgchartEmployeesStore from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureView/Employees/store";
import {
  fetchEmployeeOrgchart,
  processChildrenNodes,
  IEmployeeInOrgchartChild,
} from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureView/Employees/api";
// eslint-disable-next-line
import { getAutoLayoutWorker } from "../../workers/getOrgchartWebWorker";

interface IBottomHandle {
  nodeId: string;
}

const BottomHandle: FC<IBottomHandle> = ({ nodeId }) => {
  const [shouldFetch, setShouldFetch] = useState(false);
  const nodes = useOrgchartEmployeesStore((state) => state.nodes);
  const { setCenter, getNode } = useReactFlow();
  const companyId = useCompanyId();
  const structureId = useStructureStore((state) => state.structureId) || "";
  const store = useOrgchartEmployeesStore.getState();

  const hasChildren = useMemo(
    () => nodes.some((n) => n.data.supervisorId === nodeId),
    [nodes, nodeId]
  );

  const { data, isLoading } = useInfiniteQuery({
    queryKey: ["orgchartEmployees", companyId, structureId, nodeId],
    queryFn: ({ pageParam }) =>
      fetchEmployeeOrgchart(companyId, structureId, pageParam, nodeId),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor || undefined,
    enabled: shouldFetch,
  });

  const workerRef = useRef<Worker | null>(null);

  useEffect(() => {
    if (!workerRef.current) {
      workerRef.current = getAutoLayoutWorker();
    }

    return () => {
      workerRef.current?.terminate();
      workerRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (data?.pages[0] && shouldFetch) {
      const { nodes: newNodes, edges: newEdges } = processChildrenNodes(
        data.pages[0],
        nodeId
      );

      const existingNodes = store.nodes;
      const existingEdges = store.edges;
      const existingNodeIds = new Set(existingNodes.map((n) => n.id));
      const existingEdgeIds = new Set(existingEdges.map((e) => e.id));

      const filteredNodes = newNodes.filter((n) => !existingNodeIds.has(n.id));
      const filteredEdges = newEdges.filter((e) => !existingEdgeIds.has(e.id));

      if (filteredNodes.length > 0 && workerRef.current) {
        const mergedNodes = [...existingNodes, ...filteredNodes];
        const mergedEdges = [...existingEdges, ...filteredEdges];

        workerRef.current.postMessage({
          nodes: mergedNodes,
          edges: mergedEdges,
        });
        workerRef.current.onmessage = (event) => {
          store.setAllNodes(event.data.nodes);
          store.setNodes(event.data.nodes);
          store.setEdges(event.data.edges);

          // Center on middle child
          setTimeout(() => {
            if (filteredNodes.length > 0) {
              const middleIndex = Math.floor(filteredNodes.length / 2);
              const xValue = filteredNodes.length % 2;
              const targetNode = getNode(filteredNodes[middleIndex].id);

              if (targetNode?.position) {
                setCenter(
                  xValue === 0
                    ? targetNode.position.x
                    : targetNode.position.x + 170,
                  targetNode.position.y,
                  {
                    zoom: 0.6,
                    duration: 800,
                  }
                );
              }
            }
          }, 400);
        };
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, nodeId, shouldFetch]);

  const onHandleClick = () => {
    store.setAllNodes(store.nodes);
    store.setSearchedNodeId(null);

    if (hasChildren) {
      // Remove descendants
      const allNodes = store.nodes;
      const allEdges = store.edges;
      const descendantIds = new Set<string>();

      const findDescendants = (parentId: string) => {
        allEdges.forEach((edge) => {
          if (edge.source === parentId) {
            descendantIds.add(edge.target);
            findDescendants(edge.target);
          }
        });
      };
      findDescendants(nodeId);

      const filteredNodes = allNodes.filter((n) => !descendantIds.has(n.id));
      const filteredEdges = allEdges.filter(
        (e) => !descendantIds.has(e.source) && !descendantIds.has(e.target)
      );

      // Clear pagination cursors for collapsed node and all its descendants
      const newCursors = new Map(store.paginationCursors);
      newCursors.delete(nodeId);
      descendantIds.forEach((id) => newCursors.delete(id));
      useOrgchartEmployeesStore.setState({ paginationCursors: newCursors });

      // Reset search state so pagination uses normal (non-exclusion) path after re-expand
      store.setWasSearchTriggered(false);
      store.setHierarchyData(null);

      store.setAllNodes(filteredNodes);
      store.setNodes(filteredNodes);
      store.setEdges(filteredEdges);

      setShouldFetch(false);
    } else {
      // Fetch children - remove this node from hierarchy so it's not excluded
      if (store.hierarchyData) {
        const removeNode = (
          children: IEmployeeInOrgchartChild[],
          targetId: string
        ): IEmployeeInOrgchartChild[] =>
          children.filter((child) => {
            if (child.employeeId === targetId) {
              return false;
            }
            if (child.children?.length) {
              // eslint-disable-next-line
              child.children = removeNode(child.children, targetId);
            }
            return true;
          });

        const updatedHierarchy = {
          ...store.hierarchyData,
          children: removeNode(store.hierarchyData.children, nodeId),
        };
        store.setHierarchyData(updatedHierarchy);
      }

      setShouldFetch(true);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.2, delay: 0.4 } }}
    >
      <Handle
        type="source"
        position={Position.Bottom}
        onClick={onHandleClick}
        id="a"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "background-color .3s ease-in-out",
          backgroundColor: !hasChildren ? "#2196F3" : "#BBDEFB",
          padding: "4px 10px",
          borderRadius: "5px",
          width: "100px",
          height: "auto",
          border: "none",
          cursor: "pointer",
          boxShadow:
            "0px 1px 5px 0px rgba(0, 0, 0, 0.12), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 3px 1px -2px rgba(0, 0, 0, 0.20)",
        }}
      >
        <Box sx={{ display: "flex" }}>
          {isLoading ? (
            <CircularProgress size="1rem" />
          ) : (
            <>
              <Typography
                sx={{
                  fontWeight: "fontWeightMedium",
                  fontSize: "12px",
                  color: !hasChildren ? "white" : "black",
                }}
              >
                {__(!hasChildren ? "Rozwiń" : "Zwiń")}
              </Typography>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="19"
                height="18"
                viewBox="0 0 19 18"
                fill="none"
                style={{
                  transition: "rotate .3s ease-in-out",
                  rotate: !hasChildren ? "180deg" : "0deg",
                }}
              >
                <path
                  d="M9.5 6.22119L5 10.7212L6.0575 11.7787L9.5 8.34369L12.9425 11.7787L14 10.7212L9.5 6.22119Z"
                  fill={!hasChildren ? "white" : "black"}
                />
              </svg>
            </>
          )}
        </Box>
      </Handle>
    </motion.div>
  );
};

export default BottomHandle;
